import { useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { Form, Select, Input, Space, Button, Checkbox, Card, Modal, Segmented, Typography } from 'antd';
import { isBefore, isValid } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { createUseStyles } from 'react-jss';
import { ShipiumTag } from '../../../shared/ShipiumTag';
import { useOtsEntitled } from '~/hooks/useOtsEntitled';
import DatePicker from '../../../dateFnsComponents/DatePicker';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { FulfillmentContextDetail } from '../../../../store/services/FP_FulfillmentContextService/types';
import type { FormInstance } from 'antd/es/form/Form';
import type { HolidaysTypes } from 'date-holidays';
import Holidays from 'date-holidays';
import { CountryCodeDropdown, ALL_COUNTRY_VALUE } from '../../../shared/CountryCodeDropdown';
import DurationInputNumber from '../../../shared/DurationInputNumber';
import { useMinuteTick } from '~/hooks/useMinuteTick';
import type { DurationTypes } from '../../../types';
import {
  defaultOtsOverrideExpiration,
  getOriginTimeZoneId,
  hoursToDecimalDaysDisplayParser,
  hoursDaysDisplayFormatter,
  hoursToDecimalDaysDisplayFormatter,
} from '../../utils';

const useStyles = createUseStyles({
  otsControls: {
    marginBottom: 24,
    '& > .ant-form-item': { marginBottom: 8 },
    '& > .ant-form-item:last-child': { marginBottom: 0 },
  },
  otsCard: {
    marginBottom: 24,
  },
  overrideSection: {
    borderLeft: '3px solid #e8e8e8',
    paddingLeft: 16,
    marginTop: 20,
  },
  overrideHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
  },
  overrideSubtitle: {
    marginBottom: 12,
  },
});

// The Segmented value type is string|number, so the stored boolean is mapped through
// getValueProps/normalize rather than bound directly. `override` is the truthy side because the
// mode control is a second view of otsOverrideEnabled, not a state field of its own.
const MODE_SEGMENT = { override: 'override', calculated: 'calculated' } as const;
const toSegment = (val?: boolean) => ({ value: val ? MODE_SEGMENT.override : MODE_SEGMENT.calculated });
const fromSegment = (val: string | number) => val === MODE_SEGMENT.override;

export const DayOfWeek = {
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
  SUNDAY: 'SUNDAY',
} as const satisfies Record<string, string>;

export const SHIPPING_DAYS = Object.values(DayOfWeek);
export const SUPPORTED_HOLIDAY_COUNTRIES = ['US', 'MX'];

const { RangePicker, TimePicker } = DatePicker;
const { Option } = Select;

export interface ScheduleEntryFormProps {
  context?: FulfillmentContextDetail;
  form: FormInstance;
  excludeName?: boolean;
  excludeRange?: boolean;
  initialValues?: any;
  allowOtsOverride?: boolean;
  excludeOts?: boolean;
}

export const getAllHolidays = () => {
  return SUPPORTED_HOLIDAY_COUNTRIES.reduce((list: HolidaysTypes.Holiday[], countryCode: string) => {
    return [...list, ...new Holidays(countryCode).getHolidays()];
  }, []);
};
export const getHolidaysForCountry = (countryCode: string) => {
  return new Holidays(countryCode).getHolidays();
};
export const getHolidatesForNextNYears = (countryCode: string, numberOfYears: number) => {
  const holidaysObject = new Holidays(countryCode);
  let fullList: HolidaysTypes.Holiday[] = [];
  for (let i = 0; i < numberOfYears; i++) {
    fullList = [...fullList, ...holidaysObject.getHolidays(new Date().getFullYear() + i)];
  }

  // only show holidates that haven't passed
  return fullList.filter((d) => d.end.getTime() >= Date.now());
};

export const ScheduleEntryForm = ({
  context,
  form,
  excludeName,
  initialValues,
  excludeRange,
  allowOtsOverride,
  excludeOts,
}: ScheduleEntryFormProps) => {
  const selectedCountryCode = Form.useWatch('countryCode', form);
  const expectedDaysAndTimeToShip = Form.useWatch('expectedDaysAndTimeToShip', form);
  const shippingDays = Form.useWatch('shippingDays', form);
  const otsOverrideEnabled = Form.useWatch('otsOverrideEnabled', form);
  const otsOverrideDaysAndTimeToShip = Form.useWatch('otsOverrideDaysAndTimeToShip', form);
  const otsOverrideExpiration = Form.useWatch('otsOverrideExpiration', form);

  const otsEnabled = useOtsEntitled();
  // Order-to-ship is a whole-card presence, not a per-schedule setting: where the card renders the
  // schedule is eligible and the expected days field is a fallback, and where it does not there is
  // nothing to fall back FROM, so that same field is the only ship time the schedule has.
  const showOtsCard = otsEnabled && !excludeOts;

  // The OTS card does not exist until entitlement resolves, and rc-field-form merges form-level
  // `initialValues` into the store only on the Form's FIRST render — a later prop change updates
  // `this.initialValues` and nothing else. So a field that registers when the card finally appears
  // gets nothing, and an entry with a stored override would show as Calculated.
  //
  // This lives here rather than in either caller because this component is the one that knows
  // entitlement and decides whether the card mounts; doing it per-caller left the two of them
  // asymmetric. Layout, not passive, so the correction lands before the browser paints. Guarded on
  // the mode field still being unset, so it can never overwrite a choice the user has made.
  useLayoutEffect(() => {
    if (!showOtsCard || !initialValues || form.getFieldValue('otsOverrideEnabled') !== undefined) {
      return;
    }
    const {
      otsOverrideEnabled: seeded,
      otsOverrideDaysAndTimeToShip: value,
      otsOverrideExpiration: until,
    } = initialValues;
    if (seeded === undefined) {
      return;
    }
    form.setFieldsValue({
      otsOverrideEnabled: seeded,
      otsOverrideDaysAndTimeToShip: value,
      otsOverrideExpiration: until,
    });
  }, [form, initialValues, showOtsCard]);

  const [timeMode, setTimeMode] = useState<DurationTypes>('Time');
  const [overrideTimeMode, setOverrideTimeMode] = useState<DurationTypes>('Time');
  const classes = useStyles();

  // A schedule can serve origins in several zones, so this is the zone of the origin the user came in
  // through, not the only one the override reaches. Without it the picker falls back to browser-local,
  // so a resolved expiry is always displayed alongside the zone it was resolved in.
  const originTimeZoneId = getOriginTimeZoneId(context);
  const expirationZone = originTimeZoneId ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const expirationZoneLabel = originTimeZoneId
    ? `${originTimeZoneId} (${context?.origin?.origin?.name})`
    : expirationZone;

  // Any interaction with the picker, including clearing it, retires the default for the life of the
  // form. Matches V2LimitForm's namedTouched and UsePreviousHistoricalShipments' datePickerTouched.
  const [expirationTouched, setExpirationTouched] = useState(false);

  // A removal confirmed in this session, but not yet saved. The saved override is still what is being
  // served, so switching away from it must keep confirming; what changes is that re-adding one is now
  // a NEW override rather than the entry's own, and must not inherit its end date.
  const [overrideRemoved, setOverrideRemoved] = useState(false);

  // Comparing against a render-time `new Date()` would leave the status tag reporting an expired
  // override as active for as long as the form sits open, since time passing is not a render trigger.
  const now = useMinuteTick();
  // A malformed stored value parses to an Invalid Date, which `isBefore` reports as not-expired and
  // `format` throws on — taking the page down. An unusable date is treated as no expiration at all.
  const expiration = otsOverrideExpiration && isValid(otsOverrideExpiration) ? otsOverrideExpiration : undefined;
  const overrideExpired = !!expiration && isBefore(expiration, now);

  // What the ENTRY arrived with, which is a different question from what the form currently holds.
  // The store only ever answers "what will be saved": a value the user has just typed, one they have
  // cleared, and one left behind by a previously selected entry all read identically there. Anything
  // describing or deciding against the state a shipper is being served right now has to come from
  // here instead — reading the store for it is what produced the cross-entry leak and a confirmation
  // dialog that quoted an unsaved edit as live configuration.
  const savedOverride =
    initialValues?.otsOverrideEnabled === true
      ? {
          hours: initialValues.otsOverrideDaysAndTimeToShip as number | undefined,
          expiration:
            initialValues.otsOverrideExpiration && isValid(initialValues.otsOverrideExpiration)
              ? (initialValues.otsOverrideExpiration as Date)
              : undefined,
        }
      : undefined;
  const savedOverrideEnded = !!savedOverride?.expiration && isBefore(savedOverride.expiration, now);

  // Until a value exists the override is configured but serving nothing, so a status tag would be
  // claiming a state the form cannot save yet.
  //
  // The tag says when the override runs out, not that it is "active" — the word would sit a
  // centimetre from the mode control and invite reading it as the other segment.
  const overrideStatusTag = () => {
    if (!otsOverrideEnabled || otsOverrideDaysAndTimeToShip === undefined || otsOverrideDaysAndTimeToShip === null) {
      return null;
    }
    if (overrideExpired) {
      return (
        <ShipiumTag color="Coral">
          Ended {formatInTimeZone(expiration as Date, expirationZone, 'MMM d')} · override no longer applied
        </ShipiumTag>
      );
    }
    return (
      <ShipiumTag color="Gold">
        {expiration ? `Until ${formatInTimeZone(expiration, expirationZone, 'MMM d')}` : 'No end date'}
      </ShipiumTag>
    );
  };

  const overrideValueExtra = () => {
    if (otsOverrideDaysAndTimeToShip === 0) {
      return (
        <div>
          <b>Ships same day</b> (0 Hours · 0.00 Days)
        </div>
      );
    }
    if (otsOverrideDaysAndTimeToShip === undefined || otsOverrideDaysAndTimeToShip === null) {
      return null;
    }
    return (
      <div>
        {otsOverrideDaysAndTimeToShip} Hours · {hoursToDecimalDaysDisplayFormatter(otsOverrideDaysAndTimeToShip)} Days
      </div>
    );
  };

  const expectedDaysField = (
    <Form.Item
      style={{ verticalAlign: 'middle', width: '360px' }}
      label={<b>{showOtsCard ? 'Expected Days To Ship (Fallback)' : 'Expected Days To Ship'}</b>}
      name="expectedDaysAndTimeToShip"
      tooltip={
        showOtsCard
          ? "Used only when Shipium isn't calculating the Expected Days To Ship."
          : "This only matters if you use Delivery promise. If not, add arbitrary value such as '0'"
      }
      rules={[{ required: true }]}
      extra={
        <div>
          {timeMode !== 'Time' ? (
            <>
              {hoursDaysDisplayFormatter(expectedDaysAndTimeToShip)}
              <br />
            </>
          ) : null}
          {timeMode !== 'Hours' ? (
            <>
              {expectedDaysAndTimeToShip} Hours
              <br />
            </>
          ) : null}
          {timeMode !== 'Days' ? (
            <>
              {hoursToDecimalDaysDisplayFormatter(expectedDaysAndTimeToShip)} Days
              <br />
            </>
          ) : null}
          {/* Always on screen, but it means a different thing in each mode — and in Override it is
              the answer to "what do I lapse back into", which is why it stays visible there rather
              than making someone flip the mode control to read it. */}
          {showOtsCard ? (
            // An override past its end date is not being served, so this field means exactly what it
            // means under Calculated — future tense about the override ending would describe a moment
            // that has already passed.
            otsOverrideEnabled && !overrideExpired ? (
              // An override with no end date never ends, so promising it will would be describing a
              // moment that is not coming.
              expiration ? (
                <>What this schedule returns to when the override ends.</>
              ) : (
                <>What this schedule returns to if you remove the override.</>
              )
            ) : (
              <>Served only when Shipium has nothing calculated.</>
            )
          ) : null}
        </div>
      }
    >
      <DurationInputNumber timeMode={timeMode} setTimeMode={setTimeMode} />
    </Form.Item>
  );

  const hasWeekendShipping = useMemo(() => {
    if (!shippingDays || !Array.isArray(shippingDays)) {
      return false;
    }
    return shippingDays.includes(DayOfWeek.SATURDAY) || shippingDays.includes(DayOfWeek.SUNDAY);
  }, [shippingDays]);

  useEffect(() => {
    if (shippingDays && Array.isArray(shippingDays) && !hasWeekendShipping) {
      form.setFieldValue('weekendCutoff', undefined);
    }
  }, [hasWeekendShipping, form, shippingDays]);

  const allHolidayList = useMemo(() => getAllHolidays(), []);
  const selectedHolidayList = useMemo(() => getHolidaysForCountry(selectedCountryCode), [selectedCountryCode]);

  const onShippingTimeChange = (e: Date) => {
    form.setFieldsValue({
      shippingCutoff: e,
    });
  };

  const onWeekendTimeChange = (e: Date) => {
    form.setFieldsValue({
      weekendCutoff: e,
    });
  };

  // convert API expectedDaysAndTimeToShip 2.71 days decmial to 65 hours
  //
  // Derived into a new object, never assigned onto the caller's. This runs on EVERY render, and the
  // conversion is not idempotent — mutating in place re-converts an already-converted value and
  // compounds it ×24 per render (1 → 24 → 576 → 13824). A caller that memoizes its initialValues is
  // all it takes.
  const formInitialValues = useMemo(
    () =>
      initialValues
        ? {
            ...initialValues,
            expectedDaysAndTimeToShip: hoursToDecimalDaysDisplayParser(initialValues.expectedDaysAndTimeToShip || '0'),
          }
        : initialValues,
    [initialValues],
  );

  return (
    <Form form={form} layout="vertical" initialValues={formInitialValues}>
      {!excludeName && (
        <Form.Item
          name={'name'}
          style={{ verticalAlign: 'middle' }}
          label={<b>Schedule Name</b>}
          rules={[
            {
              required: true,
              message: 'Name is required.',
            },
          ]}
          extra={'How will you refer to this schedule.'}
        >
          <Input type={'text'} />
        </Form.Item>
      )}
      <Form.Item label={<b>Shipping Days</b>} name="shippingDays" rules={[{ required: true }]}>
        <Select mode="multiple">
          {SHIPPING_DAYS.map((val) => (
            <Select.Option value={val}>{val}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      {showOtsCard ? (
        <Card size="small" className={classes.otsCard} title={<b>Order-to-ship</b>}>
          {allowOtsOverride && (
            <>
              {/* No label: the card header already says "Order-to-ship", and the segments name
                  themselves. A label here would either repeat that or, worse, shorten it — "ship time"
                  reads as the ShipTime service rather than as order-to-ship time. */}
              <div className={classes.overrideHeader}>
                {/* noStyle: this sits in a flex row beside the status tag, and the default
                    Form.Item wrapper adds a bottom margin and reserves a validation row, which
                    pushes the tag out of alignment and opens a gap under the control. */}
                <Form.Item name="otsOverrideEnabled" noStyle getValueProps={toSegment} normalize={fromSegment}>
                  <Segmented
                    options={[
                      { label: 'Calculated', value: MODE_SEGMENT.calculated },
                      { label: 'Override', value: MODE_SEGMENT.override },
                    ]}
                    onChange={(value) => {
                      // Switching away from a SAVED override destroys something shippers are being
                      // served right now, and the destruction is invisible until save. Confirm it,
                      // and use the moment to answer the question the end date raises on its own —
                      // whether anyone has to act on that date — rather than explaining it in
                      // standing prose. An override typed in this session is not confirmed: nothing
                      // live is being removed, so a dialog would only interrupt someone deciding.
                      if (value === MODE_SEGMENT.calculated && savedOverride) {
                        // Quote the SAVED override, never the live field: an unsaved edit is not what
                        // shippers are being served, and a cleared one is not "same day".
                        const servedNow = hoursDaysDisplayFormatter(savedOverride.hours);
                        const on = savedOverride.expiration
                          ? `${formatInTimeZone(savedOverride.expiration, expirationZone, 'EEE, MMM d yyyy')} at ${formatInTimeZone(
                              savedOverride.expiration,
                              expirationZone,
                              'h:mm a',
                            )}`
                          : undefined;
                        // An override past its end date is already not being served, so telling
                        // someone they are about to stop serving it is simply untrue — and the
                        // question they need answered is the opposite one: whether removing it
                        // changes anything. It does not, beyond clearing the stored value.
                        const content = savedOverrideEnded
                          ? `This override ended on ${on}, so this schedule already serves the calculated order to ship time. Removing it clears the stored value and changes nothing a shipper sees.`
                          : `This schedule serves ${servedNow} today. ${
                              on
                                ? `It would have returned to the calculated order to ship time by itself on ${on}.`
                                : 'It has no end date, so it would not have ended on its own.'
                            } Removing it returns to the calculated order to ship time as soon as you save.`;
                        // Put the control back before asking, so it does not sit on Calculated while
                        // the question is still open, and apply the change only once confirmed.
                        form.setFieldValue('otsOverrideEnabled', true);
                        Modal.confirm({
                          title: 'Remove the override?',
                          content,
                          okText: 'Remove override',
                          okType: 'danger',
                          cancelText: 'Cancel',
                          onOk: () => {
                            setOverrideRemoved(true);
                            form.setFieldValue('otsOverrideEnabled', false);
                          },
                        });
                        return;
                      }
                      // Seeding here rather than in an effect is what makes a re-default impossible on
                      // mount, on load and on save — a change handler cannot fire in any of them.
                      if (value !== MODE_SEGMENT.override || expirationTouched) {
                        return;
                      }
                      // Only the override that ARRIVED with the entry keeps its own end date, because
                      // an empty one there is a deliberate permanent opt-out and stamping three days
                      // onto it would silently turn "never expires" into "expires Thursday".
                      //
                      // Every other case is an override being configured now and gets the default —
                      // including one the user removed a moment ago in this session, which is why the
                      // test is the saved state rather than "is there a value in the store". The store
                      // still holds the removed override's end date, and re-adopting it would hand
                      // back a date that has very possibly already passed.
                      if (savedOverride && !overrideRemoved) {
                        return;
                      }
                      form.setFieldValue('otsOverrideExpiration', defaultOtsOverrideExpiration(originTimeZoneId));
                    }}
                  />
                </Form.Item>
                {overrideStatusTag()}
              </div>
              <div className={classes.overrideSubtitle}>
                <Typography.Text type="secondary">
                  {otsOverrideEnabled
                    ? 'Serve your own order to ship time instead of the calculated one. Leave the end date empty to keep it indefinitely.'
                    : 'Shipium calculates this schedule’s order to ship time from recent shipments.'}
                </Typography.Text>
              </div>
            </>
          )}
          <div className={classes.otsControls}>
            {/* Hidden rather than unmounted under Override, because an override ignores tags but the
                setting still applies the moment one lapses or is removed. `preserve` keeps an
                unmounted field's value in the store, but getFieldsValue() reports only REGISTERED
                fields, so unmounting drops it from the payload and formatOtsProcessingFlags falls
                back to the stored entry — discarding an unticked box on the way past. */}
            <Form.Item name="otsTagsEnabled" valuePropName="checked" hidden={!!otsOverrideEnabled}>
              <Checkbox>Use shipment tags to improve the estimate</Checkbox>
            </Form.Item>
          </div>
          {allowOtsOverride && otsOverrideEnabled && (
            <div className={classes.overrideSection}>
              <Form.Item
                style={{ verticalAlign: 'middle', width: '360px' }}
                label={<b>Order to ship time to serve instead</b>}
                name="otsOverrideDaysAndTimeToShip"
                rules={[{ required: true, message: 'An override value is required.' }]}
                extra={overrideValueExtra()}
              >
                <DurationInputNumber allowEmpty timeMode={overrideTimeMode} setTimeMode={setOverrideTimeMode} />
              </Form.Item>
              <Form.Item
                style={{ verticalAlign: 'middle' }}
                label={<b>Override lasts until</b>}
                name="otsOverrideExpiration"
                // The form value stays a true instant; only the picker sees origin wall-clock, so the
                // serializer and everything comparing against `now` are unaffected by the zone.
                getValueProps={(value: Date | undefined) => ({
                  value: value && isValid(value) ? toZonedTime(value, expirationZone) : value,
                })}
                normalize={(value: Date | undefined) => (value ? fromZonedTime(value, expirationZone) : value)}
                extra={
                  expiration ? (
                    <>
                      {`${overrideExpired ? 'Ended' : 'Ends'} ${formatInTimeZone(
                        expiration,
                        expirationZone,
                        'EEE, MMM d yyyy',
                      )} at ${formatInTimeZone(expiration, expirationZone, 'h:mm a')} ${expirationZoneLabel}`}
                      {/* Which value replaced the override depends on whether a calculated one exists for
                          the origin, so the two possibilities are named rather than one of them asserted. */}
                      {overrideExpired ? (
                        <>
                          <br />
                          Now serving the calculated order to ship time, or your fallback if none is available.
                        </>
                      ) : null}
                    </>
                  ) : (
                    // An empty date is now the primary way to opt out permanently, not a field
                    // someone forgot to fill in, so it reads as a statement rather than a warning.
                    <Typography.Text type="secondary">
                      No end date — a permanent opt-out. Shipium keeps measuring this schedule, but serves your value
                      until you switch back to Calculated.
                    </Typography.Text>
                  )
                }
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="yyyy-MM-dd HH:mm"
                  onChange={() => setExpirationTouched(true)}
                />
              </Form.Item>
            </div>
          )}
          {expectedDaysField}
        </Card>
      ) : (
        expectedDaysField
      )}
      <Form.Item
        style={{ verticalAlign: 'middle' }}
        label={<b>Shipping Cutoff</b>}
        name="shippingCutoff"
        rules={[{ required: true }]}
      >
        <TimePicker
          onSelect={(e) => onShippingTimeChange(e)}
          showNow={false}
          showMinute={true}
          showSecond={false}
          format="HH:mm"
        />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.shippingDays !== currentValues.shippingDays}
      >
        {({ getFieldValue }) => {
          const shippingDays = getFieldValue('shippingDays') || [];
          const hasWeekend = shippingDays.includes(DayOfWeek.SATURDAY) || shippingDays.includes(DayOfWeek.SUNDAY);

          return (
            <Form.Item
              style={{ verticalAlign: 'middle' }}
              label={<b>Weekend Cutoff</b>}
              name="weekendCutoff"
              rules={[{ required: hasWeekend }]}
              hidden={!hasWeekend}
            >
              <TimePicker
                onSelect={(e) => onWeekendTimeChange(e)}
                showNow={false}
                showMinute={true}
                showSecond={false}
                format="HH:mm"
              />
            </Form.Item>
          );
        }}
      </Form.Item>
      {!excludeRange && (
        <Form.Item
          style={{ verticalAlign: 'middle' }}
          label={<b>Effective Dates</b>}
          name="effectiveDates"
          tooltip="If you don’t have a specific interval, we recommend setting artificially far into the future as you do not need to update it"
          rules={[{ required: true }]}
        >
          <RangePicker />
        </Form.Item>
      )}
      <Space>
        <Form.Item name={'configuredHolidays'} style={{ verticalAlign: 'middle' }} label={<b>Holidays</b>}>
          <Select showSearch={true} mode="multiple" dropdownMatchSelectWidth={false} style={{ minWidth: 150 }}>
            {(selectedCountryCode && ALL_COUNTRY_VALUE !== selectedCountryCode
              ? selectedHolidayList
              : allHolidayList
            ).map((val) => (
              <Option key={val.name} value={val.name}>
                {val.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          tooltip={'Select a specific country to view their national holidays in the dropdown'}
          initialValue={ALL_COUNTRY_VALUE}
          name={'countryCode'}
          style={{ verticalAlign: 'middle' }}
          label={<b>Country</b>}
        >
          <CountryCodeDropdown allowAll={true} allowedCountries={SUPPORTED_HOLIDAY_COUNTRIES} />
        </Form.Item>
      </Space>
      <Form.Item style={{ verticalAlign: 'middle' }} label={<b>Custom Holidays</b>}>
        <Form.List name="holidays">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => {
                return (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    {/* A dated row with no name is a stored holiday that was written without one. */}
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.holidays !== next.holidays}>
                      {() => (
                        <Form.Item {...restField} name={[name, 'name']}>
                          <Input
                            placeholder={
                              form.getFieldValue(['holidays', name, 'date']) ? 'Unnamed holiday' : 'Holiday Name'
                            }
                          />
                        </Form.Item>
                      )}
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'date']}
                      rules={[{ required: true, message: 'Missing Date' }]}
                    >
                      <DatePicker placeholder="Select Date" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                );
              })}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Holiday
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>
    </Form>
  );
};
